import React, { useContext } from 'react';
import { useModal } from '@gumtree/shell/src/use-modal';
import { LoginModalType } from '@gumtree/shell/src/reducers/common/login-modal';
import Icon from '@gumtree/ui-library/src/icon/icon';
import { Button } from '@gumtree/ui-library';
import { useFeatureIsOn, useFeatureValue } from '@growthbook/growthbook-react';
import { GrowthBookFeature } from '@gumtree/shared/src/model/experiment';
import type { L1Category } from '@gumtree/shared/src/types/client-data';
import { trackGA4Event } from '@gumtree/shared/src/util/ga4-shared';
import { RequestAQuoteFormContext } from '../../vip-context';
import {
    SellerPhoneNumber,
    SellerPhoneNumberTitle,
    SellerPhoneRevealPanel,
} from './seller-phone-reveal.style';

export default function SellerPhoneReveal({
    isUserLoggedIn,
    pro,
    containsCallTrackingFeature,
    phoneNumber,
    onClick,
    isDisabled,
    revealNumberUrl,
    revealNumberUnmasked,
    showDataQueue,
    isStickyHeader,
    haveRevealedNumber,
    l1CategorySeoName,
    requestId,
    adId,
    skipLoginToReveal,
}: Props) {
    const { openDialog } = useModal();
    const { adSource, openRequestAQuoteForm } = useContext(RequestAQuoteFormContext);

    function revealPhone(e: React.MouseEvent) {
        trackGA4Event<any>({
            event: 'ad_click_button_info',
            requestId,
            listingId: adId
        });
   
        if (adSource === 'Bark') {
            if (!isUserLoggedIn) {
                openDialog(e, LoginModalType.REQUEST_QUOTE, true);
            } else {
                openRequestAQuoteForm('call');
            }
            return;
        }

        if (skipLoginToReveal || isUserLoggedIn) {
            onClick(e);
        } else {
            const url = new URL(location.href);
            url.searchParams.set('revealPhone', 'true');
            history.replaceState({}, '', url);
            openDialog(e, LoginModalType.REVEAL_PHONE, true);
        }
    }

    const inExperimentGTCON331 = useFeatureIsOn(GrowthBookFeature.REVEAL_NUMBER_LABEL);
    const isUI_202506 = useFeatureValue(GrowthBookFeature.SERVICE_NEW_UI, 'A') === 'B' && l1CategorySeoName === 'business-services';
    const showNewPhoneLabel =
        (inExperimentGTCON331 && l1CategorySeoName === 'flats-houses') ||
        (inExperimentGTCON331 && l1CategorySeoName === 'jobs') ||
        (inExperimentGTCON331 && l1CategorySeoName === 'cars-vans-motorbikes' && !pro) || isUI_202506;
    
    const phoneLabel = adSource === 'Bark' 
        ? 'Request a call back' 
        : showNewPhoneLabel ? 'Show phone number' : 'Reveal';

    return (
        <SellerPhoneRevealPanel>
            {pro && containsCallTrackingFeature ? (
                // `haveRevealedNumber` is driven by redux via `revealPhone`
                haveRevealedNumber ? (
                    <SellerPhoneNumber>
                        <Icon type="phone" />
                        <SellerPhoneNumberTitle
                            data-q={
                                !isStickyHeader
                                    ? 'seller-phone-number'
                                    : 'reply-panel-reveal-btn'
                            }
                        >
                            {phoneNumber}
                        </SellerPhoneNumberTitle>
                    </SellerPhoneNumber>
                ) : (
                    <SellerPhoneRevealPanel isPro>
                        <Icon type="phone" />
                        <a className="link" href={revealNumberUrl} onClick={revealPhone}>
                            {(skipLoginToReveal || isUserLoggedIn) ? 'Click' : 'Login'} to reveal phone number
                        </a>
                    </SellerPhoneRevealPanel>
                )
            ) : (
                <SellerPhoneRevealPanel fullWidthButton={showNewPhoneLabel || adSource === 'Bark'}>
                    {!showNewPhoneLabel && adSource !== 'Bark' && (
                        <SellerPhoneNumber>
                            <Icon type="phone" />
                            <SellerPhoneNumberTitle
                                data-q={
                                    !isStickyHeader
                                        ? 'seller-phone-number'
                                        : 'reply-panel-reveal-btn'
                                }
                            >
                                {phoneNumber}
                            </SellerPhoneNumberTitle>
                        </SellerPhoneNumber>
                    )}

                    {
                        // `revealNumberUnmasked` comes from model (requires refresh)
                        (revealNumberUnmasked === '' || adSource === 'Bark') && (
                            <a href={revealNumberUrl} onClick={revealPhone}>
                                <Button
                                    display="secondary"
                                    label={
                                        showNewPhoneLabel && haveRevealedNumber
                                            ? phoneNumber
                                            : phoneLabel
                                    }
                                    disabled={showNewPhoneLabel ? false : isDisabled}
                                    icon={(showNewPhoneLabel || adSource === 'Bark') ? <Icon type={isUI_202506 ? 'phoneCall' : 'phone'} /> : <></>}
                                    fullWidth={showNewPhoneLabel || adSource === 'Bark'}
                                    {...(showDataQueue && {
                                        'data-q': 'reply-panel-reveal-btn',
                                    })}
                                />
                            </a>
                        )
                    }
                </SellerPhoneRevealPanel>
            )}
        </SellerPhoneRevealPanel>
    );
}

interface Props {
    containsCallTrackingFeature: boolean;
    haveRevealedNumber?: boolean;
    isDisabled?: boolean;
    isStickyHeader: boolean;
    isUserLoggedIn?: boolean;
    skipLoginToReveal?: boolean;
    loginThenComeBack(): void;
    onClick(e: React.MouseEvent): void;
    phoneNumber: string;
    pro: boolean;
    revealNumberUnmasked?: string;
    revealNumberUrl: string;
    showDataQueue: boolean;
    l1CategorySeoName: L1Category;
    requestId: string;
    adId: string;
}

SellerPhoneReveal.defaultProps = {
    haveRevealedNumber: false,
    isDisabled: false,
    isUserLoggedIn: false,
    revealNumberUnmasked: '',
};
